import { mutationBoundary } from '@theme/br-cart-cro-mutation';
import { replacePlan, snapshot } from '@theme/br-cart-cro-switch-core';
import { morphSection } from '@theme/section-renderer';

const key = 'br-cart-cro-operation:v1';
const root = document.documentElement;
const configNode = document.querySelector('[data-br-cart-switch-messages]');
const messages = configNode ? JSON.parse(configNode.textContent) : null;
const isEnabledTheme = () => ['main', 'unpublished'].includes(window.Shopify?.theme?.role);
const storageWorks = () => {
  try {
    localStorage.setItem(`${key}:probe`, '1');
    localStorage.removeItem(`${key}:probe`);
    return true;
  } catch {
    return false;
  }
};
let active = false;
let changed = false;
let reviewed = null;
const record = () => {
  try {
    const value = localStorage.getItem(key);
    if (value) root.dataset.brCartOperationKnown = 'true';
    else delete root.dataset.brCartOperationKnown;
    return value;
  } catch {
    return root.hasAttribute('data-br-cart-operation-known') ? 'unreadable' : null;
  }
};
const gate = (stage = 'review') => {
  root.dataset.brCartRecovery = stage;
};
const clear = () => {
  localStorage.removeItem(key);
  delete root.dataset.brCartOperationKnown;
  delete root.dataset.brCartRecovery;
};
const save = (value) => {
  localStorage.setItem(key, JSON.stringify(value));
  root.dataset.brCartOperationKnown = 'true';
  gate(active ? 'switching' : 'review');
};
const hash = async (value) =>
  Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value))))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
async function request(path, body) {
  const abort = new AbortController();
  const timeout = setTimeout(() => abort.abort(), 12000);
  try {
    const response = await fetch(`${window.Shopify.routes.root}cart/${path}.js`, {
      ...(body
        ? {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          }
        : {}),
      cache: 'no-store',
      signal: abort.signal,
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(`Cart request failed (${response.status})`);
    }
    return data;
  } finally {
    clearTimeout(timeout);
  }
}
// Shopify's cart read route is /cart.js, unlike mutation routes /cart/add.js.
async function readCart() {
  const abort = new AbortController();
  const timeout = setTimeout(() => abort.abort(), 12000);
  try {
    const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
      cache: 'no-store',
      signal: abort.signal,
    });
    if (!response.ok) throw new Error('Cart unavailable');
    return await response.json();
  } finally {
    clearTimeout(timeout);
  }
}
let refreshSequence = 0;
async function refreshSection(id) {
  const url = new URL(window.location.href);
  url.hash = '';
  url.searchParams.set('section_id', id);
  url.searchParams.set('_br_cart_refresh', `${Date.now()}-${++refreshSequence}`);
  const abort = new AbortController();
  const timeout = setTimeout(() => abort.abort(), 12000);
  try {
    // Bypass both HTTP history cache and the renderer's shared pending/cache maps.
    const response = await fetch(url.href, { cache: 'no-store', signal: abort.signal });
    if (!response.ok) throw new Error('Cart section unavailable');
    const html = await response.text();
    // Await validation and DOM replacement before allowing checkout again.
    await morphSection(id, html, { mode: id === 'cart-drawer-section' ? 'hydration' : 'full' });
  } finally {
    clearTimeout(timeout);
  }
}
async function refresh() {
  const ids = [
    ...new Set(
      [...document.querySelectorAll('cart-items-component[data-section-id]')].map(
        (node) => node.dataset.sectionId
      )
    ),
  ];
  for (const id of ids) await refreshSection(id);
  const cart = await readCart();
  // Reuse Horizon's public badge renderer without dispatching a second mutation.
  for (const icon of document.querySelectorAll('cart-icon'))
    icon.renderCartBubble?.(cart.item_count, false);
}
// History and peer completion run outside execute's already-held exclusive lock.
async function refreshReconciled() {
  const reconcile = async () => {
    for (let attempt = 0; attempt < 3; attempt++) {
      const before = snapshot(await readCart());
      await refresh();
      if (before === snapshot(await readCart())) return;
    }
    throw new Error('Cart changed while restoring');
  };
  // New switches are omitted without Web Locks; baseline history still gets
  // bounded before/after verification instead of exposing stale checkout HTML.
  if (!navigator.locks) return reconcile();
  return navigator.locks.request('br-cart-cro-mutation', { mode: 'exclusive' }, reconcile);
}
function notice(text, allowContinue = false, dismissible = false) {
  const cartPath = `${window.Shopify.routes.root}cart`.replace(/\/$/, '');
  const isCartPage = location.pathname.replace(/\/$/, '') === cartPath;
  if (!isCartPage)
    document
      .querySelectorAll('#MainContent > [data-br-cart-switch-notice]')
      .forEach((node) => node.remove());
  for (const parent of [
    isCartPage ? document.querySelector('#MainContent') : null,
    document.querySelector('.cart-drawer__inner'),
  ].filter(Boolean)) {
    let node = parent.querySelector(':scope > [data-br-cart-switch-notice]');
    if (!node) {
      node = document.createElement('div');
      node.dataset.brCartSwitchNotice = '';
      node.className = 'br-cart-cro-review';
      if (parent.matches('.cart-drawer__inner') || !document.querySelector('.cart-drawer__inner'))
        node.setAttribute('role', 'status');
      parent.prepend(node);
    }
    node.className =
      dismissible && !record() ? 'br-cart-cro__confirmation type-small' : 'br-cart-cro-review';
    if (parent.matches('.cart-drawer__inner'))
      node.classList.add('br-cart-cro__notice', 'type-small');
    node.replaceChildren();
    if (dismissible && !record()) {
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'br-cart-cro__confirmation-close';
      close.setAttribute('aria-label', messages.dismiss);
      close.textContent = '×';
      close.addEventListener('click', () => {
        const target = parent.querySelector(
          '[data-br-cart-switch] button, button[name="checkout"]:not(:disabled), a[href], button:not(:disabled):not(.br-cart-cro__confirmation-close)'
        );
        if (target) target.focus();
        else {
          parent.setAttribute('tabindex', '-1');
          parent.focus();
        }
        document
          .querySelectorAll('.br-cart-cro__confirmation')
          .forEach((banner) => banner.remove());
      });
      node.append(close);
    }
    const p = document.createElement('p');
    p.textContent =
      record() && (!navigator.locks || record() === 'unreadable')
        ? messages.recoveryUnavailable || text
        : text;
    node.append(p);
    if (record()) {
      const link = document.createElement('a');
      link.href = `${window.Shopify.routes.root}cart`;
      link.textContent = messages.review;
      node.append(link);
      if (
        allowContinue &&
        navigator.locks &&
        record() !== 'unreadable' &&
        location.pathname.replace(/\/$/, '').endsWith('/cart')
      ) {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = messages.continue;
        button.addEventListener('click', acknowledge);
        node.append(button);
      }
    }
  }
}
async function acknowledge() {
  if (!navigator.locks) return;
  return navigator.locks.request('br-cart-cro-mutation', { ifAvailable: true }, async (lock) => {
    if (!lock) {
      notice(messages.busy);
      return;
    }
    await acknowledgeLocked();
  });
}
async function acknowledgeLocked() {
  try {
    const cart = await readCart();
    if (snapshot(cart) !== reviewed) {
      await recover();
      return;
    }
    clear();
    reviewed = null;
    document.querySelectorAll('[data-br-cart-switch-notice]').forEach((node) => node.remove());
  } catch {
    notice(messages.unavailable);
  }
}
async function recover() {
  if (!record() || active) return;
  gate();
  try {
    const before = await readCart();
    await refresh();
    const cart = await readCart();
    if (snapshot(before) !== snapshot(cart)) {
      reviewed = null;
      notice(messages.recovery);
      return;
    }
    if (!record() || active) return;
    // Explicitly review every actual line, quantity and native plan detail on /cart.
    reviewed = snapshot(cart);
    notice(messages.recovery, true);
  } catch {
    notice(messages.unavailable);
  }
}
async function execute(offer) {
  if (!navigator.locks) return;
  return navigator.locks.request('br-cart-cro-mutation', { ifAvailable: true }, async (lock) => {
    if (!lock) {
      notice(messages.busy);
      return;
    }
    await executeLocked(offer);
  });
}
async function executeLocked(offer) {
  if (
    active ||
    record() ||
    root.hasAttribute('data-br-cart-recovery') ||
    !isEnabledTheme() ||
    !window.BRCartFeatures?.enabled('switch')
  )
    return;
  const owner = mutationBoundary.acquire();
  if (!owner) {
    notice(messages.busy);
    return;
  }
  active = true;
  changed = false;
  gate('switching');
  document.querySelectorAll('cart-items-component, product-form-component').forEach((node) => {
    node.inert = true;
  });
  notice(messages.switching);
  let resolved = false;
  try {
    const result = await replacePlan({
      offer,
      read: readCart,
      add: () =>
        request('add', {
          items: [{ id: offer.variantId, selling_plan: offer.planId, quantity: 1 }],
        }),
      remove: (id) => request('change', { id, quantity: 0 }),
      save,
      clear: () => {
        resolved = true;
      },
      hash,
      signalChanged: () => changed,
    });
    await refresh();
    if (resolved) clear();
    notice(
      result.state === 'success'
        ? offer.successLabel || messages.success
        : result.state === 'original'
          ? messages.original
          : messages.recovery,
      false,
      result.state === 'success' || result.state === 'original'
    );
  } catch {
    if (record()) gate();
    else delete root.dataset.brCartRecovery;
    notice(messages.recovery);
  } finally {
    active = false;
    mutationBoundary.release(owner);
    document.querySelectorAll('cart-items-component, product-form-component').forEach((node) => {
      node.inert = false;
    });
    if (record()) await recover();
    else delete root.dataset.brCartRecovery;
    mount();
    const heading = document.querySelector('#cart-drawer-heading');
    if (heading) {
      heading.tabIndex = -1;
      heading.focus();
    }
  }
}
function mount() {
  if (
    !isEnabledTheme() ||
    !messages ||
    root.hasAttribute('data-br-cart-recovery') ||
    !storageWorks() ||
    !navigator.locks
  )
    return;
  for (const node of document.querySelectorAll('[data-br-cart-switch-offer]')) {
    if (node.dataset.bound) continue;
    if (!window.BRCartFeatures?.enabled('switch') || record()) continue;
    node.dataset.bound = 'true';
    try {
      const offer = JSON.parse(node.textContent);
      const button = document.createElement('button');
      button.type = 'button';
      const row = document.createElement('div');
      row.className = 'br-cart-cro__switch type-small';
      const copy = document.createElement('div');
      const prompt = document.createElement('strong');
      prompt.textContent = offer.prompt || messages.prompt;
      const description = document.createElement('p');
      description.textContent = `${offer.description || messages.description} · ${offer.priceLabel}`;
      copy.append(prompt, description);
      const detailText = [offer.savingsLabel, offer.equivalentLabel].filter(Boolean).join(' · ');
      if (detailText) {
        const detail = document.createElement('p');
        if (offer.savingsEmphasis && detailText.startsWith(offer.savingsEmphasis)) {
          const emphasis = document.createElement('strong');
          emphasis.textContent = offer.savingsEmphasis;
          detail.className = 'type-emphasis-medium';
          detail.append(
            emphasis,
            document.createTextNode(detailText.slice(offer.savingsEmphasis.length))
          );
        } else detail.textContent = detailText;
        copy.append(detail);
      }
      button.className = 'br-cart-cro__switch-action type-small';
      button.textContent = offer.actionLabel || messages.switch;
      button.setAttribute(
        'aria-label',
        `${offer.actionLabel || messages.switch}: ${offer.description || messages.description} · ${offer.priceLabel}`
      );
      row.append(copy, button);
      button.addEventListener('click', () => execute(offer));
      node.after(row);
    } catch {
      /* Invalid or absent verified offer has no mutation UI. */
    }
  }
}
if (isEnabledTheme() && messages) {
  for (const event of [
    'shopify:cart:lines-update',
    'shopify:cart:discount-update',
    'shopify:cart:note-update',
  ]) {
    document.addEventListener(event, (update) => {
      if (active) changed = true;
      else if (record()) {
        reviewed = null;
        gate();
        update.promise?.finally(() => recover()).catch(() => {});
      }
    });
  }
  window.addEventListener('storage', (event) => {
    if (event.key !== key) return;
    if (active) {
      changed = true;
      gate('switching');
      return;
    }
    if (event.newValue) {
      root.dataset.brCartOperationKnown = 'true';
      gate();
      recover();
    } else {
      // Another tab resolved the operation; render its final cart before checkout.
      gate();
      refreshReconciled()
        .then(() => {
          if (active || record()) return;
          delete root.dataset.brCartRecovery;
          document
            .querySelectorAll('[data-br-cart-switch-notice]')
            .forEach((node) => node.remove());
          mount();
        })
        .catch(() => notice(messages.unavailable));
    }
  });
  // HTTP-cached history documents and bfcache can both contain stale cart HTML.
  const historyRestore = () =>
    performance.getEntriesByType?.('navigation')[0]?.type === 'back_forward' ||
    performance.navigation?.type === 2;
  let restoring = false;
  const reconcileHistory = async () => {
    if (restoring) return;
    restoring = true;
    reviewed = null;
    gate();
    try {
      if (record()) await recover();
      else {
        await refreshReconciled();
        if (active || record()) return;
        delete root.dataset.brCartRecovery;
        document.querySelectorAll('[data-br-cart-switch-notice]').forEach((node) => node.remove());
        mount();
      }
    } catch {
      notice(messages.unavailable);
    } finally {
      restoring = false;
    }
  };
  window.addEventListener('pageshow', (event) => {
    if (event.persisted || historyRestore()) reconcileHistory();
  });
  // Start at module bootstrap too: pageshow may already have fired after a slow import.
  if (historyRestore()) reconcileHistory();
  document.addEventListener('br:cart-review-change', mount);
  new MutationObserver(mount).observe(document.body, { childList: true, subtree: true });
  mount();
  if (record() && !restoring) recover();
}
